import * as DialogPrimitive from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';
import { IconClose } from './icons';
import { cn, focusRing } from './utils';

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl';

const SIZES: Record<DialogSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-3xl',
  xl: 'max-w-5xl',
};

export interface DialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: ReactNode;
  /** Descrição do propósito do diálogo. Vira `aria-describedby`. */
  readonly description?: ReactNode;
  readonly size?: DialogSize;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  /** Impede fechar por Esc / clique fora. Use apenas em operação em andamento. */
  readonly dismissible?: boolean;
  readonly className?: string;
  /** Rótulo do botão de fechar. Padrão: "Fechar". */
  readonly closeLabel?: string;
  readonly hideCloseButton?: boolean;
}

/**
 * Diálogo modal.
 *
 * O Radix já garante o essencial de A11y: foco preso dentro do diálogo, foco
 * devolvido ao gatilho ao fechar, `aria-modal`, e o resto da página marcado com
 * `aria-hidden`. O que acrescentamos:
 *  · `title` e `description` são obrigatórios no contrato de tipo (o title
 *    sempre; a description quando o diálogo pede uma decisão) — diálogo sem
 *    nome acessível é anunciado como "diálogo" e nada mais;
 *  · `dismissible=false` para operações em andamento: fechar no meio de um ato
 *    assinado deixaria a prova de assinatura queimada sem efeito registrado.
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  size = 'md',
  children,
  footer,
  dismissible = true,
  className,
  closeLabel = 'Fechar',
  hideCloseButton = false,
}: DialogProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-overlay bg-[rgb(15_23_42/0.55)] backdrop-blur-[1px]',
            'animate-overlay-in',
          )}
        />
        <DialogPrimitive.Content
          /* Quando não há descrição, declaramos a ausência explicitamente para
             não deixar um `aria-describedby` apontando para nada. Com descrição,
             a associação é feita pelo próprio Radix e não pode ser sobrescrita. */
          {...(description ? {} : { 'aria-describedby': undefined })}
          onEscapeKeyDown={(event) => {
            if (!dismissible) event.preventDefault();
          }}
          onPointerDownOutside={(event) => {
            if (!dismissible) event.preventDefault();
          }}
          onInteractOutside={(event) => {
            if (!dismissible) event.preventDefault();
          }}
          className={cn(
            'fixed left-1/2 top-1/2 z-modal w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2',
            'flex max-h-[calc(100vh-3rem)] flex-col overflow-hidden rounded-lg border border-border bg-surface-raised shadow-overlay',
            'animate-content-in',
            SIZES[size],
            className,
          )}
        >
          <header className="flex items-start justify-between gap-4 border-b border-border-subtle px-5 py-4">
            <div className="min-w-0 flex-1">
              <DialogPrimitive.Title className="text-lg font-semibold text-content">
                {title}
              </DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="mt-1 text-sm text-content-muted">
                  {description}
                </DialogPrimitive.Description>
              ) : null}
            </div>
            {!hideCloseButton && dismissible ? (
              <DialogPrimitive.Close
                aria-label={closeLabel}
                className={cn(
                  'flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-content-subtle',
                  'hover:bg-surface-sunken hover:text-content',
                  focusRing,
                )}
              >
                <IconClose size={18} />
              </DialogPrimitive.Close>
            ) : null}
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

          {footer ? (
            <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-border-subtle bg-surface-sunken px-5 py-3.5">
              {footer}
            </footer>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;
